"use client";

import { format, eachDayOfInterval, addDays } from "date-fns";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import type { PricingData } from "./availability-calendar";

const formatINR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

interface PriceBreakdownProps {
  checkIn: Date;
  checkOut: Date;
  pricingData?: PricingData;
  basePrice?: number;
  taxRate?: number;
  advancePercent?: number;
}

export function PriceBreakdown({
  checkIn,
  checkOut,
  pricingData = {},
  basePrice = 30000,
  taxRate = 0.18,
  advancePercent = 50,
}: PriceBreakdownProps) {
  const nights = eachDayOfInterval({
    start: checkIn,
    end: addDays(checkOut, -1),
  });

  const nightlyPrices = nights.map((day) => {
    const key = format(day, "yyyy-MM-dd");
    return {
      date: day,
      price: pricingData[key] ?? basePrice,
    };
  });

  const subtotal = nightlyPrices.reduce((sum, n) => sum + n.price, 0);
  const taxes = Math.round(subtotal * taxRate);
  const total = subtotal + taxes;
  const advance = Math.round(total * (advancePercent / 100));
  const balance = total - advance;

  return (
    <div className="space-y-4">
      <h3 className="font-playfair text-lg font-semibold text-foreground">
        Price Breakdown
      </h3>

      <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1 scrollbar-thin">
        {nightlyPrices.map(({ date, price }) => (
          <div
            key={date.toISOString()}
            className="flex items-center justify-between text-sm"
          >
            <span className="text-muted-foreground">
              {format(date, "EEE, MMM d")}
            </span>
            <span className="font-medium text-foreground">
              {formatINR.format(price)}
            </span>
          </div>
        ))}
      </div>

      <Separator className="bg-border/50" />

      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Subtotal ({nights.length} {nights.length === 1 ? "night" : "nights"})
          </span>
          <span className="font-medium text-foreground">
            {formatINR.format(subtotal)}
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Taxes ({(taxRate * 100).toFixed(0)}% GST)
          </span>
          <span className="font-medium text-foreground">
            {formatINR.format(taxes)}
          </span>
        </div>
      </div>

      <Separator className="bg-border/50" />

      <div className="flex items-center justify-between">
        <span className="font-semibold text-foreground">Total</span>
        <span className="text-lg font-bold text-primary">
          {formatINR.format(total)}
        </span>
      </div>

      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">
            Pay now ({advancePercent}% advance)
          </span>
          <span className="text-lg font-bold text-primary">
            {formatINR.format(advance)}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Balance due before check-in</span>
          <span className="font-medium text-foreground">
            {formatINR.format(balance)}
          </span>
        </div>
      </div>
    </div>
  );
}

export function calculatePricing(
  checkIn: Date,
  checkOut: Date,
  pricingData: PricingData = {},
  basePrice = 30000,
  taxRate = 0.18,
  advancePercent = 50
) {
  const nights = eachDayOfInterval({
    start: checkIn,
    end: addDays(checkOut, -1),
  });

  const subtotal = nights.reduce((sum, day) => {
    const key = format(day, "yyyy-MM-dd");
    return sum + (pricingData[key] ?? basePrice);
  }, 0);

  const taxes = Math.round(subtotal * taxRate);
  const total = subtotal + taxes;
  const advance = Math.round(total * (advancePercent / 100));

  return { nights: nights.length, subtotal, taxes, total, advance, balance: total - advance };
}
